
import React, { useState, useEffect, useCallback, useContext, useRef } from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    GridLegacy as Grid,
    Typography,
    Button,
    Stack,
    Divider,
    FormControlLabel,
    Checkbox,
    Radio,
    RadioGroup,
    FormControl,
    FormGroup,

} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import { useAdvancedSearchParams } from "@/hooks/useAdvancedSearchParams";
import DateField from "@/components/editable/EditableField/fields/DateField";
import { IBookingAdvancedSearch, VehicleStatus, BookingStatus } from "@/logic/api";
import { SessionContext } from "@/utility/useSession";
import { ReleaseFlag, useReleaseManagement } from "@/utility/feature-management";


interface AdvancedSearchOptionsAccordionProps {
    expanded: boolean;
    hasResults: boolean;
    onApplyFilters: (filters: IBookingAdvancedSearch) => void;
    searchText: string;
    isReload?: boolean;
}

interface DraftFilters {
    searchByRef: boolean;
    searchByVehicleReg: boolean;
    searchBySurname: boolean;
    searchByTrackingId: boolean;

    statusNotCheckedInOrPreCheckIn: boolean;
    statusCheckedIn: boolean;
    statusCheckedOut: boolean;
    statusCancelled: boolean;

    checkedInFrom: string;
    checkedInTo: string;
    checkedOutFrom: string;
    checkedOutTo: string;

    allCarParks: boolean;
    exactMatch: boolean;
}

const extractDatePart = (date: string) => {
    return date.split("T")[0];
};

const getUTCStartofDay = (date: string) => {
    return `${extractDatePart(date)}T00:00:00.000Z`;
};

const getUTCEndofDay = (date: string) => {
    return `${extractDatePart(date)}T23:59:59.999Z`;
};

const getDateFromString = (dateString: string) => {
    if (!dateString) return null;
    const datePart = extractDatePart(dateString);
    return datePart ? new Date(datePart) : null;
};

const AdvancedSearchOptionsAccordion = ({
    expanded = false,
    hasResults = true,
    onApplyFilters,
    searchText,
    isReload = false,
}: AdvancedSearchOptionsAccordionProps) => {
    const { t } = useTranslation();
    const { filters, updateFilters, resetFilters } = useAdvancedSearchParams();
    const session = useContext(SessionContext);
    const isScanByExternalReferenceReleased = useReleaseManagement(ReleaseFlag.releaseAdditionalExternalReferences);
    const isBookingSearchMatchTypeReleased = useReleaseManagement(ReleaseFlag.releaseBookingSearchMatchType);

    const [isExpanded, setIsExpanded] = useState(expanded || !hasResults);

    useEffect(() => {
        const handleBeforeUnload = () => {
            resetFilters();
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [resetFilters]);

    // Reset filters whenever a new search text is submitted or when reload is triggered
    const previousSearchRef = useRef<string>(searchText);
    const previousReloadRef = useRef<boolean>(isReload);
    useEffect(() => {
        if (previousSearchRef.current !== searchText || (isReload && !previousReloadRef.current)) {
            resetFilters();
            previousSearchRef.current = searchText;
        }
        previousReloadRef.current = isReload;
    }, [searchText, isReload, resetFilters]);

    const scanOptions = session.current?.site?.preferences?.bookingScanOptions?.properties;
    const useTrackingId = session.current?.site?.preferences?.use_tracking_id;
    const isUuidEnabled = scanOptions?.enableUuid ?? false;
    const isRefEnabled = scanOptions?.enableRef ?? false;
    const isExternalReferencesEnabled = isScanByExternalReferenceReleased && (scanOptions?.enableExternalReferences ?? false);
    const isTrackingIdEnabled = isExternalReferencesEnabled && session.current?.site?.preferences?.use_tracking_id;

    const filtersToDraftData = (filters: IBookingAdvancedSearch): DraftFilters => ({
        searchByRef: filters.searchBy.ref,
        searchByVehicleReg: filters.searchBy.vehicleRegistration,
        searchBySurname: filters.searchBy.lastName,
        searchByTrackingId: filters.searchBy.trackingId || false,

        statusNotCheckedInOrPreCheckIn: filters.filterBy.vehicleStatus.includes(VehicleStatus.NotCheckedIn) ||
            filters.filterBy.vehicleStatus.includes(VehicleStatus.PreCheckIn),
        statusCheckedIn: filters.filterBy.vehicleStatus.includes(VehicleStatus.CheckedIn),
        statusCheckedOut: filters.filterBy.vehicleStatus.includes(VehicleStatus.CheckedOut),
        statusCancelled: filters.filterBy.bookingStatus.includes(BookingStatus.Cancelled),

        checkedInFrom: filters.filterBy.checkedIn?.from?.split("T")[0] || "",
        checkedInTo: filters.filterBy.checkedIn?.to?.split("T")[0] || "",
        checkedOutFrom: filters.filterBy.checkedOut?.from?.split("T")[0] || "",
        checkedOutTo: filters.filterBy.checkedOut?.to?.split("T")[0] || "",

        allCarParks: filters.allCarParks,
        exactMatch: filters.exactMatch ?? true
    });

    const draftDataToFilters = (data: DraftFilters): IBookingAdvancedSearch => {
        const vehicleStatus: VehicleStatus[] = [];
        const bookingStatus: BookingStatus[] = [];

        if (data.statusNotCheckedInOrPreCheckIn) {
            vehicleStatus.push(VehicleStatus.NotCheckedIn);
            vehicleStatus.push(VehicleStatus.PreCheckIn);
        }
        if (data.statusCheckedIn) vehicleStatus.push(VehicleStatus.CheckedIn);
        if (data.statusCheckedOut) vehicleStatus.push(VehicleStatus.CheckedOut);
        if (data.statusCancelled) bookingStatus.push(BookingStatus.Cancelled);

        const result: IBookingAdvancedSearch = {
            searchBy: {
                ref: data.searchByRef,
                vehicleRegistration: data.searchByVehicleReg,
                lastName: data.searchBySurname,
                trackingId: isTrackingIdEnabled ? data.searchByTrackingId : false,
                uuid: isUuidEnabled,
                altRef: isRefEnabled
            },
            filterBy: {
                vehicleStatus,
                bookingStatus
            },
            allCarParks: data.allCarParks,
            exactMatch: data.exactMatch
        };

        if (data.checkedInFrom || data.checkedInTo) {
            result.filterBy.checkedIn = {
                from: data.checkedInFrom ? getUTCStartofDay(data.checkedInFrom) : undefined,
                to: data.checkedInTo ? getUTCEndofDay(data.checkedInTo) : undefined
            };
        }

        if (data.checkedOutFrom || data.checkedOutTo) {
            result.filterBy.checkedOut = {
                from: data.checkedOutFrom ? getUTCStartofDay(data.checkedOutFrom) : undefined,
                to: data.checkedOutTo ? getUTCEndofDay(data.checkedOutTo) : undefined
            };
        }

        return result;
    };

    const [draftData, setDraftData] = useState<DraftFilters>(filtersToDraftData(filters));

    useEffect(() => {
        if (!hasResults) {
            setIsExpanded(true);
        }
    }, [hasResults]);

    useEffect(() => {
        setDraftData(filtersToDraftData(filters));
    }, [filters]);

    const handleAccordionChange = (_event: React.SyntheticEvent, expanded: boolean) => {
        setIsExpanded(expanded);
    };

    const handleApplyFilters = useCallback(() => {
        const newFilters = draftDataToFilters(draftData);
        updateFilters(newFilters);
        onApplyFilters?.(newFilters);
    }, [draftData, updateFilters, onApplyFilters]);

    const handleResetFilters = useCallback(() => {
        resetFilters();
    }, [resetFilters]);

    const handleFieldChange = <K extends keyof DraftFilters>(field: K, value: DraftFilters[K]) => {
        setDraftData(prev => ({ ...prev, [field]: value }));
    };

    const currentDraftFilters = draftDataToFilters(draftData);
    const hasChanges = JSON.stringify(currentDraftFilters) !== JSON.stringify(filters);

    return (
        <Box sx={{ backgroundColor: "transparent", borderRadius: 1, mb: 2 }} data-testid="search-results-advanced-search">
            <Accordion
                expanded={isExpanded}
                onChange={handleAccordionChange}
                sx={{
                    boxShadow: "none",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon color="action" />}
                    sx={{
                        backgroundColor: "#f3f4f680",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        borderTopLeftRadius: 4,
                        borderTopRightRadius: 4,
                    }}
                    aria-controls="advanced-search-content"
                    id="advanced-search-header"
                    data-testid="advanced-search-accordion-header"
                >
                    <Typography variant="body1" fontWeight={500}>
                        {t("home.advancedSearch.title")}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" sx={{ mb: 2, color: "text.primary", fontWeight: "bold" }}>
                                {t("home.advancedSearch.searchBy")}
                            </Typography>
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={draftData.searchByRef}
                                            onChange={(e) => handleFieldChange("searchByRef", e.target.checked)}
                                            sx={{ color: "brand.main", "&.Mui-checked": { color: "brand.main" } }}
                                            data-testid="search-by-booking-ref-checkbox"
                                        />
                                    }
                                    label={t("home.advancedSearch.bookingReference")}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={draftData.searchByVehicleReg}
                                            onChange={(e) => handleFieldChange("searchByVehicleReg", e.target.checked)}
                                            sx={{ color: "brand.main", "&.Mui-checked": { color: "brand.main" } }}
                                            data-testid="search-by-vehicle-reg-checkbox"
                                        />
                                    }
                                    label={t("home.advancedSearch.vehicleRegistration")}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={draftData.searchBySurname}
                                            onChange={(e) => handleFieldChange("searchBySurname", e.target.checked)}
                                            sx={{ color: "brand.main", "&.Mui-checked": { color: "brand.main" } }}
                                            data-testid="search-by-surname-checkbox"
                                        />
                                    }
                                    label={t("home.advancedSearch.surname")}
                                />
                                {isExternalReferencesEnabled && useTrackingId && (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={draftData.searchByTrackingId}
                                                onChange={(e) => handleFieldChange("searchByTrackingId", e.target.checked)}
                                                sx={{ color: "brand.main", "&.Mui-checked": { color: "brand.main" } }}
                                                data-testid="search-by-tracking-id-checkbox"
                                            />
                                        }
                                        label={t("home.advancedSearch.trackingId")}
                                    />
                                )}
                            </FormGroup>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" sx={{ mb: 2, color: "text.primary", fontWeight: "bold", }}>
                                {t("home.advancedSearch.bookingStatus")}
                            </Typography>
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={draftData.statusNotCheckedInOrPreCheckIn}
                                            onChange={(e) => handleFieldChange("statusNotCheckedInOrPreCheckIn", e.target.checked)}
                                            sx={{ color: "brand.main", "&.Mui-checked": { color: "brand.main" } }}
                                            data-testid="booking-status-not-checked-in-checkbox"
                                        />
                                    }
                                    label={t("home.advancedSearch.notCheckedIn")}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={draftData.statusCheckedIn}
                                            onChange={(e) => handleFieldChange("statusCheckedIn", e.target.checked)}
                                            sx={{ color: "brand.main", "&.Mui-checked": { color: "brand.main" } }}
                                            data-testid="booking-status-checked-in-checkbox"
                                        />
                                    }
                                    label={t("home.advancedSearch.checkedIn")}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={draftData.statusCheckedOut}
                                            onChange={(e) => handleFieldChange("statusCheckedOut", e.target.checked)}
                                            sx={{ color: "brand.main", "&.Mui-checked": { color: "brand.main" } }}
                                            data-testid="booking-status-checked-out-checkbox"
                                        />
                                    }
                                    label={t("home.advancedSearch.checkedOut")}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={draftData.statusCancelled}
                                            onChange={(e) => handleFieldChange("statusCancelled", e.target.checked)}
                                            sx={{ color: "brand.main", "&.Mui-checked": { color: "brand.main" } }}
                                            data-testid="booking-status-cancelled-checkbox"
                                        />
                                    }
                                    label={t("home.advancedSearch.cancelled")}
                                />
                            </FormGroup>
                        </Grid>

                        {draftData.statusCheckedIn && (
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" sx={{ mb: 2, color: "text.primary", fontWeight: "bold", }}>
                                    {t("home.advancedSearch.checkedIn")}
                                </Typography>
                                <Grid container spacing={1}>
                                    <Grid item xs={12} sm={6}>
                                        <DateField
                                            label={t("home.advancedSearch.from")}
                                            value={draftData.checkedInFrom}
                                            onChange={(value) => handleFieldChange("checkedInFrom", value)}
                                            maxDate={getDateFromString(draftData.checkedInTo) || undefined}
                                            testId="checked-in-from-date"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <DateField
                                            label={t("home.advancedSearch.to")}
                                            value={draftData.checkedInTo}
                                            onChange={(value) => handleFieldChange("checkedInTo", value)}
                                            minDate={getDateFromString(draftData.checkedInFrom) || undefined}
                                            testId="checked-in-to-date"
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        )}

                        {draftData.statusCheckedOut && (
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" sx={{ mb: 2, color: "text.primary", fontWeight: "bold", }}>
                                    {t("home.advancedSearch.checkedOut")}
                                </Typography>
                                <Grid container spacing={1}>
                                    <Grid item xs={12} sm={6}>
                                        <DateField
                                            label={t("home.advancedSearch.from")}
                                            value={draftData.checkedOutFrom}
                                            onChange={(value) => handleFieldChange("checkedOutFrom", value)}
                                            maxDate={getDateFromString(draftData.checkedOutTo) || undefined}
                                            testId="checked-out-from-date"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <DateField
                                            label={t("home.advancedSearch.to")}
                                            value={draftData.checkedOutTo}
                                            onChange={(value) => handleFieldChange("checkedOutTo", value)}
                                            minDate={getDateFromString(draftData.checkedOutFrom) || undefined}
                                            testId="checked-out-to-date"
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        )}

                        {/* Unfortunately we need this to force the following grid on it's own row */}
                        <Grid item xs={12} />

                        <Grid item xs={12} md={isBookingSearchMatchTypeReleased ? 6 : 12}>
                            <Typography variant="h6" sx={{ mb: 2, color: "text.primary", fontWeight: "bold", }}>
                                {t("home.advancedSearch.carPark")}
                            </Typography>
                            <FormControl component="fieldset">
                                <RadioGroup
                                    row
                                    value={draftData.allCarParks ? "all" : "this"}
                                    onChange={(e) => handleFieldChange("allCarParks", e.target.value === "all")}
                                    data-testid="car-park-radio-group"
                                    sx={{ gap: 2 }}
                                >
                                    <FormControlLabel
                                        value="this"
                                        control={
                                            <Radio
                                                sx={{
                                                    color: "brand.main",
                                                    "&.Mui-checked": { color: "brand.main" }
                                                }}
                                                data-testid="this-car-park-radio"
                                            />
                                        }
                                        label={t("home.advancedSearch.thisCarParkOnly")}
                                    />
                                    <FormControlLabel
                                        value="all"
                                        control={
                                            <Radio
                                                sx={{
                                                    color: "brand.main",
                                                    "&.Mui-checked": { color: "brand.main" }
                                                }}
                                                data-testid="all-car-parks-radio"
                                            />
                                        }
                                        label={t("home.advancedSearch.allCarParks")}
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Grid>

                        {isBookingSearchMatchTypeReleased && (
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" sx={{ mb: 2, color: "text.primary", fontWeight: "bold", }}>
                                    {t("home.advancedSearch.matchType")}
                                </Typography>
                                <FormControl component="fieldset">
                                    <RadioGroup
                                        row
                                        value={draftData.exactMatch ? "exact" : "partial"}
                                        onChange={(e) => handleFieldChange("exactMatch", e.target.value === "exact")}
                                        data-testid="match-type-radio-group"
                                        sx={{ gap: 2 }}
                                    >
                                        <FormControlLabel
                                            value="exact"
                                            control={
                                                <Radio
                                                    sx={{
                                                        color: "brand.main",
                                                        "&.Mui-checked": { color: "brand.main" }
                                                    }}
                                                    data-testid="exact-match-radio"
                                                />
                                            }
                                            label={t("home.advancedSearch.exactMatch")}
                                        />
                                        <FormControlLabel
                                            value="partial"
                                            control={
                                                <Radio
                                                    sx={{
                                                        color: "brand.main",
                                                        "&.Mui-checked": { color: "brand.main" }
                                                    }}
                                                    data-testid="partial-match-radio"
                                                />
                                            }
                                            label={t("home.advancedSearch.partialMatch")}
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                            <Stack
                                direction="row"
                                spacing={2}
                                justifyContent="flex-end"
                            >
                                <Button
                                    variant="outlined"
                                    onClick={handleResetFilters}
                                    data-testid="advanced-search-reset-button"
                                    disableRipple
                                    disableElevation
                                    sx={{
                                        borderColor: (theme) => theme.palette.grey[400],
                                        transition: "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1), border-color 150ms cubic-bezier(.4,0,.2,1), box-shadow 150ms ease",
                                        "&:hover": {
                                            backgroundColor: (theme) => theme.palette.grey[100],
                                            borderColor: (theme) => theme.palette.grey[500],
                                        },
                                        "&:active": {
                                            transform: "scale(0.97)",
                                        },
                                        "&:focus-visible": {
                                            boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}40`,
                                        },
                                    }}
                                >
                                    {t("general.clear")}
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleApplyFilters}
                                    disabled={!hasChanges}
                                    data-testid="advanced-search-apply-button"
                                    disableRipple
                                    disableElevation
                                    sx={{
                                        fontWeight: "bold",
                                        backgroundColor: (theme) => theme.palette.brand.main,
                                        color: (theme) => theme.palette.primary.contrastText,
                                        transition: "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1), box-shadow 150ms ease",
                                        "&:hover": {
                                            backgroundColor: (theme) => theme.palette.brand.dark,
                                            color: (theme) => theme.palette.primary.contrastText,
                                        },
                                        "&:active": {
                                            transform: "scale(0.97)",
                                        },
                                        "&:focus-visible": {
                                            boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}40`,
                                        },
                                    }}
                                >
                                    {t("general.apply")}
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

export default AdvancedSearchOptionsAccordion;
